import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Box } from '@chakra-ui/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import {
  ADMIN_DEFERRED_NAVIGATION_TIMEOUT_MS,
  findAdminNavigationRoute,
} from './navigationInventory'

type DeferredNavigationContextValue = {
  isPreparing: boolean
  navigateDeferred: (target: string, options?: { replace?: boolean }) => void
}

const DeferredNavigationContext =
  createContext<DeferredNavigationContextValue | null>(null)

const ADMIN_DEFERRED_PROGRESS_MIN_VISIBLE_MS = 240

type DeferredNavigationProviderProps = {
  children: ReactNode
}

const isPlainLeftClick = (event: MouseEvent): boolean =>
  event.button === 0 &&
  !event.metaKey &&
  !event.altKey &&
  !event.ctrlKey &&
  !event.shiftKey

const findAnchor = (target: EventTarget | null): HTMLAnchorElement | null => {
  if (!(target instanceof Element)) {
    return null
  }
  return target.closest('a[href]')
}

const withTimeout = async (
  promise: Promise<unknown>,
  timeoutMs: number
) => {
  let timeoutId: number | undefined
  const timeout = new Promise(resolve => {
    timeoutId = window.setTimeout(resolve, timeoutMs)
  })

  await Promise.race([promise, timeout])
  if (timeoutId !== undefined) {
    window.clearTimeout(timeoutId)
  }
}

const wait = async (durationMs: number) => {
  await new Promise(resolve => {
    window.setTimeout(resolve, durationMs)
  })
}

export function DeferredNavigationProvider({
  children,
}: DeferredNavigationProviderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const auth = useAuth()
  const { currentEnv } = useNetworkEnvironment()
  const [isPreparing, setIsPreparing] = useState(false)
  const requestIdRef = useRef(0)

  const authSnapshot = `${auth.isAuthenticated}:${auth.user?.id ?? ''}`
  const snapshotRef = useRef({
    authSnapshot,
    currentEnv,
  })

  useEffect(() => {
    window.requestAnimationFrame(() => {
      setIsPreparing(false)
    })
  }, [location.hash, location.pathname, location.search])

  useEffect(() => {
    snapshotRef.current = {
      authSnapshot,
      currentEnv,
    }
    requestIdRef.current += 1
    setIsPreparing(false)
  }, [authSnapshot, currentEnv])

  useEffect(() => {
    const cancel = () => {
      requestIdRef.current += 1
      setIsPreparing(false)
    }
    window.addEventListener('pagehide', cancel)
    window.addEventListener('pageshow', cancel)
    return () => {
      window.removeEventListener('pagehide', cancel)
      window.removeEventListener('pageshow', cancel)
    }
  }, [])

  const prepareAndNavigate = useCallback(
    async (targetUrl: URL, replace = false) => {
      const route = findAdminNavigationRoute(targetUrl.pathname)
      if (!route) {
        navigate(`${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`, {
          replace,
        })
        return
      }

      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      const startedSnapshot = snapshotRef.current
      setIsPreparing(true)
      const minimumVisibleProgress = wait(ADMIN_DEFERRED_PROGRESS_MIN_VISIBLE_MS)

      const preparation = Promise.all([
        route.preload(),
        route.prepare({
          queryClient,
          currentEnv,
          isAuthenticated: auth.isAuthenticated,
          userId: auth.user?.id ?? '',
          targetUrl,
        }),
      ])

      try {
        await withTimeout(preparation, ADMIN_DEFERRED_NAVIGATION_TIMEOUT_MS)
      } catch {
        // Degraded navigation still waits until preparation failed or timed out.
      }

      await minimumVisibleProgress

      if (
        requestIdRef.current !== requestId ||
        snapshotRef.current.authSnapshot !== startedSnapshot.authSnapshot ||
        snapshotRef.current.currentEnv !== startedSnapshot.currentEnv
      ) {
        setIsPreparing(false)
        return
      }

      navigate(`${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`, {
        replace,
      })
    },
    [
      auth.isAuthenticated,
      auth.user?.id,
      currentEnv,
      navigate,
      queryClient,
    ]
  )

  const navigateDeferred = useCallback(
    (target: string, options?: { replace?: boolean }) => {
      const targetUrl = new URL(target, window.location.href)
      void prepareAndNavigate(targetUrl, options?.replace ?? false)
    },
    [prepareAndNavigate]
  )

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || !isPlainLeftClick(event)) {
        return
      }

      const anchor = findAnchor(event.target)
      if (!anchor || anchor.dataset.deferredNavigation === 'off') {
        return
      }
      if (anchor.target && anchor.target !== '_self') {
        return
      }
      if (anchor.hasAttribute('download')) {
        return
      }

      const targetUrl = new URL(anchor.href)
      if (targetUrl.origin !== window.location.origin) {
        return
      }

      const current = new URL(window.location.href)
      const sameDocument =
        targetUrl.pathname === current.pathname &&
        targetUrl.search === current.search
      if (sameDocument) {
        return
      }

      const route = findAdminNavigationRoute(targetUrl.pathname)
      if (!route) {
        return
      }

      event.preventDefault()
      void prepareAndNavigate(targetUrl)
    }

    document.addEventListener('click', handleClick, true)
    return () => {
      document.removeEventListener('click', handleClick, true)
    }
  }, [prepareAndNavigate])

  const value = useMemo<DeferredNavigationContextValue>(
    () => ({
      isPreparing,
      navigateDeferred,
    }),
    [isPreparing, navigateDeferred]
  )

  return (
    <DeferredNavigationContext.Provider value={value}>
      <Box
        aria-hidden="true"
        position="fixed"
        top={0}
        left={0}
        right={0}
        h="4px"
        zIndex={1800}
        pointerEvents="none"
        opacity={isPreparing ? 1 : 0}
        transition="opacity 160ms ease"
        overflow="hidden"
      >
        <Box
          h="100%"
          w={isPreparing ? '80%' : '0%'}
          bg="admin.400"
          boxShadow="0 0 12px var(--chakra-colors-admin-300)"
          transition="width 900ms cubic-bezier(0.22, 1, 0.36, 1)"
        />
      </Box>
      {children}
    </DeferredNavigationContext.Provider>
  )
}

export const useDeferredNavigation = () => {
  const context = useContext(DeferredNavigationContext)
  if (!context) {
    throw new Error('useDeferredNavigation must be used inside provider')
  }
  return context
}
